import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { Turnstile, TURNSTILE_ENABLED } from "@/components/Turnstile";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Gift, Truck } from "lucide-react";

export default function Register() {
  const { register } = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get("redirect") || "/account";
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);
  const [confirmSent, setConfirmSent] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(form.password)) {
      setError("Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character.");
      return;
    }
    if (TURNSTILE_ENABLED && !token) {
      setError("Please complete the human verification.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const result = await register(form.name.trim(), form.email.trim(), form.password, token);
      // Silently subscribe the account email to offers & new arrivals.
      try { await api.post("/newsletter", { email: form.email.trim(), source: "register" }); } catch { /* non-blocking */ }
      if (result?.confirmEmail) {
        setConfirmSent(form.email.trim());
        return;
      }
      toast.success("Account created - welcome to Zebaa!");
      navigate(redirectTo);
    } catch (err) {
      setError(err.message);
      setResetSignal((n) => n + 1);
    } finally {
      setLoading(false);
    }
  };

  if (confirmSent) {
    return (
      <div className="max-w-md mx-auto px-5 py-24 min-h-[70vh] text-center" data-testid="register-confirm-sent">
        <Seo title="Confirm your email" path="/register" noindex />
        <Sparkles className="w-12 h-12 text-gold mx-auto" />
        <h1 className="font-serif text-3xl mt-5 text-charcoal">Check your inbox</h1>
        <p className="text-gray-500 mt-3">We've sent a confirmation link to <strong className="text-charcoal">{confirmSent}</strong>. Click it to activate your Zebaa account, then sign in.</p>
        <Link to="/login" className="inline-block mt-8 bg-pink text-white px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">Go to sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 py-16 min-h-[70vh]" data-testid="register-page">
      <Seo title="Create Account" path="/register" noindex />
      <Reveal className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Join Us</p>
        <h1 className="font-serif text-4xl mt-3 text-charcoal">Create Account</h1>
        <div className="w-16 h-px bg-gold mx-auto mt-5" />
      </Reveal>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-charcoal mb-1.5" htmlFor="register-name">Full name<span className="text-pink">*</span></label>
          <input id="register-name" type="text" required value={form.name} onChange={(e) => update("name", e.target.value)}
            data-testid="register-name"
            className="w-full border border-gray-300 rounded px-4 py-3.5 text-base text-charcoal focus:outline-none focus:border-charcoal focus:ring-1 focus:ring-charcoal transition-colors" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-charcoal mb-1.5" htmlFor="register-email">Email address<span className="text-pink">*</span></label>
          <input id="register-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)}
            data-testid="register-email"
            className="w-full border border-gray-300 rounded px-4 py-3.5 text-base text-charcoal focus:outline-none focus:border-charcoal focus:ring-1 focus:ring-charcoal transition-colors" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-charcoal mb-1.5" htmlFor="register-password">Password<span className="text-pink">*</span></label>
          <div className="relative flex items-stretch">
            <input id="register-password" type={showPassword ? "text" : "password"} required value={form.password} onChange={(e) => update("password", e.target.value)}
              data-testid="register-password"
              className="w-full border border-gray-300 rounded rounded-r-none border-r-0 px-4 py-3.5 text-base text-charcoal focus:outline-none focus:border-charcoal focus:ring-1 focus:ring-charcoal transition-colors" />
            <button type="button" onClick={() => setShowPassword((s) => !s)} data-testid="register-show-password"
              className="shrink-0 px-4 border border-gray-300 rounded rounded-l-none text-sm font-semibold text-pink hover:bg-pink/5 transition-colors">
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">At least 8 characters, with an uppercase &amp; lowercase letter, a number and a special character.</p>
        </div>
        {error && <p className="text-sm text-pink" data-testid="register-error">{error}</p>}

        <Turnstile onVerify={setToken} action="register" resetSignal={resetSignal} />
        <button type="submit" disabled={loading} data-testid="register-submit"
          className="w-full bg-pink text-white py-4 rounded text-base font-semibold hover:bg-pink-hover transition-colors disabled:opacity-60">
          {loading ? "Creating..." : "Create account"}
        </button>
      </form>

      <GoogleAuthButton />

      <p className="text-center text-sm text-gray-500 mt-6">
        Already have an account? <Link to="/login" data-testid="go-login" className="text-pink hover:underline">Sign in</Link>
      </p>
    </div>
  );
}
